import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { MeetingDetailsObject } from './collapsibleSchedule';

import { gradientBackground } from '../../../assets/styles/button.style';
import CalendarIcon from '../../../assets/svg/calendarPlus.svg';
import VoiceIcon from '../../../assets/svg/phoneCall.svg';
import VideoIcon from '../../../assets/svg/videoCall.svg';
import { axiosInstance } from '../../../assets/utils/axiosInstance';
import { CALL_VALUES, MEETING_RESPONSE } from '../../../constants/meetings';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import LoadingComponent from '../../common/loadingComponent';
import { formatDate } from '../../customer/meetings/meetings/meetingDetailItem';



interface MeetingDetailsItemsProps extends MeetingDetailsObject {
  refresh: () => void;
}


const styles = {
  mainStyle: {
    borderRadius: '6px',
    border: '1px solid #D1D1D1',
    padding: '16px 28px 16px 20px',
    width: '60%',

  },
  headerStyle: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 500,
    fontSize: '14px',
  },
  bodyTextStyle: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: '12px',
  },
  bodyTextStyle2: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: '14px',
    color: '#878787',
  },
  bodyTextStyle3: {
    fontFamily: 'Schibsted Grotesk',
    fontWeight: 400,
    fontSize: '12px',
    color: '#878787',
  },
  buttonStyle2: {
    color: '#181818',
    fontSize: '12px',
    paddingX: '12px',
    height: '40px',
    border: '1px solid #D1D1D1',
    fontWeight: 'normal',
    alignSelf: 'flex-start',
    textTransform: 'none',
    '&:hover': {
      color: '#181818',
      bgcolor: '#D1D1D1',
    },
  },
};

const getDateTime = (dateString: string): { date: string, time: string } => {
  const dateTime = new Date(dateString);

  if (isNaN(dateTime.getTime())) {
    // Invalid date string
    return {
      date: '',
      time: '',
    };
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };
  const time = dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const date = new Intl.DateTimeFormat('en-US', options).format(dateTime);

  return {
    time,
    date,
  };
};

const MeetingDetails = ({ 
  id,
  date,
  dateCreated, 
  organizer, 
  type, 
  refresh,
}: MeetingDetailsItemsProps ) => {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const [responseStatus, setResponseStatus] = useState<{ type: number, status: boolean }>({ type: 0, status: false });

  const updateMeetingHandler = async (meetingRes: number) => {
    setResponseStatus({ type: meetingRes, status: true });
    try {
      const res =  await axiosInstance().patch(`schedule/meetings/${id}/confirm`, {
        confirm: meetingRes,
      });
      if (res) {
        if (meetingRes === MEETING_RESPONSE.CONFIRM)
          createToastNotification(intl.formatMessage({ defaultMessage: 'Meeting has been accepted and added to the Meeting List.' }), 'success');
        else
          createToastNotification(intl.formatMessage({ defaultMessage: 'Meeting declined' }), 'info');
        refresh();
      }
    } catch (err) {
      createToastNotification(intl.formatMessage({ defaultMessage: 'Something Went Wrong' }), 'error');
    }
    setResponseStatus({ type: 0, status: false });
  };

  const getMeetingType = (callType: number): string => {
    if (callType === CALL_VALUES.VIDEO) return 'Video Call';
    if (callType === CALL_VALUES.VOICE) return 'Voice Call';
    return '';
  };

  return (
    <Stack direction='column' justifyContent='space-between'  gap={2}>
      <Stack direction='row' gap={2} alignItems='center'>
        <Typography sx={styles.headerStyle}>
          {organizer}
        </Typography>
        <Typography sx={styles.bodyTextStyle}>
          {formatDate(dateCreated)}
        </Typography>
      </Stack>
      <Stack sx={styles.mainStyle} gap={2} >
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' alignItems='center' gap={1} justifyContent={{ lg: 'flex-end', md: 'flex-start' }}>
            <Box component='img' src={CalendarIcon} sx={{ width: '20px' }} alt='Meeting Icon'/>
            <Typography sx={styles.headerStyle}>
              <FormattedMessage defaultMessage='Scheduled meet invitation:' />
            </Typography>
          </Stack>
          <Stack direction='row' alignItems='center' gap={1} justifyContent={{ lg: 'flex-end', md: 'flex-start' }}>
            <Tooltip title='Type of meet' >
              <Box component='img' src={type === CALL_VALUES.VIDEO ? VideoIcon : VoiceIcon} />
            </Tooltip>
            <Typography sx={styles.headerStyle}>
              {getMeetingType(type)}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction='row' justifyContent='space-between'>
          <Stack flexBasis='40%' gap={1}>
            <Stack direction='row'>
              <Stack flexBasis='30%'>
                <Typography sx={styles.bodyTextStyle3}>
                  <FormattedMessage defaultMessage='Date' />
                </Typography>
              </Stack>
              <Typography sx={{ ...styles.bodyTextStyle3, color: '#454545' }}>
                {getDateTime(date)?.date}
              </Typography>
            </Stack>
            <Stack direction='row'>
              <Stack flexBasis='30%'>
                <Typography sx={styles.bodyTextStyle3}>
                  <FormattedMessage defaultMessage='Time' />
                </Typography>
              </Stack>
              <Typography sx={{ ...styles.bodyTextStyle3, color: '#454545' }}>
                {getDateTime(date)?.time}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction='row' gap={1.5}>
            <Button 
              sx={styles.buttonStyle2}
              onClick={() => updateMeetingHandler(MEETING_RESPONSE.DECLINE)}
            >
              {responseStatus.type === MEETING_RESPONSE.DECLINE && !responseStatus.status ?
                <LoadingComponent size={20}/>
                : 
                intl.formatMessage({ defaultMessage: 'Decline' })
              }
            </Button>
            <Button sx={{
              ...gradientBackground,
              fontSize: '12px',
              paddingX: '12px',
              height: '40px',
              borderRadius: '6px',
            }}
            onClick={() => updateMeetingHandler(MEETING_RESPONSE.CONFIRM)}>
              {responseStatus.type === MEETING_RESPONSE.CONFIRM && responseStatus.status ?
                <LoadingComponent size={20}/>
                : 
                intl.formatMessage({ defaultMessage: 'Accept' })
              }
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MeetingDetails;